// Dra. Malu / CaptaZap v50 — suporte visual e salvamento simples de figurinhas.
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  FaCheck,
  FaCheckDouble,
  FaClock,
  FaExclamationCircle,
  FaFileAlt,
  FaUserCircle,
  FaLock,
  FaImage,
  FaMicrophone,
  FaVideo,
  FaWhatsapp,
  FaInstagram,
  FaFacebookMessenger,
  FaTelegramPlane,
  FaEllipsisV,
  FaPen,
  FaTrash,
  FaTimes,
  FaDownload,
  FaExpand,
  FaReply,
} from "react-icons/fa";

const avatarContatoCache = new Map();
const DRA_MALU_AVATAR_URL = "/dra-malu-avatar.webp";

async function buscarAvatarContato({ empresaId, telefone }) {
  const phone = normalizarTelefoneAvatar(telefone);

  if (!empresaId || !phone) return "";

  const cacheKey = `${empresaId}:${phone}`;
  if (avatarContatoCache.has(cacheKey)) {
    return avatarContatoCache.get(cacheKey);
  }

  const response = await fetch(
    `${getApiPublicBase()}/whatsapp/captazap/avatar?empresa_id=${encodeURIComponent(
      empresaId,
    )}&phone=${encodeURIComponent(phone)}`,
  );

  if (!response.ok) {
    avatarContatoCache.set(cacheKey, "");
    return "";
  }

  const data = await response.json();
  const profileUrl = getImagemUrl(data?.profileUrl || "");

  avatarContatoCache.set(cacheKey, profileUrl);

  return profileUrl;
}

function normalizarTelefoneAvatar(value) {
  return String(value || "").replace(/\D/g, "");
}

function valorBooleanoMensagem(value) {
  if (value === true || value === 1) return true;
  if (value === false || value === 0) return false;

  const texto = String(value ?? "")
    .trim()
    .toLowerCase();

  if (["true", "1", "sim", "yes", "y"].includes(texto)) return true;
  if (["false", "0", "nao", "não", "no", "n"].includes(texto)) return false;

  return null;
}

function isMensagemDoOperador(mensagem = {}, contexto = {}) {
  const fromMe = valorBooleanoMensagem(
    mensagem.fromMe ??
      mensagem.from_me ??
      mensagem.isFromMe ??
      mensagem.enviada_por_mim ??
      mensagem.sent_by_me,
  );

  // Prioridade máxima: quando o backend/webhook informa fromMe,
  // a bolha deve respeitar esse valor, independente do usuário logado.
  if (fromMe === true) return true;
  if (fromMe === false) return false;

  const direcao = String(
    mensagem.direcao ||
      mensagem.direction ||
      mensagem.sentido ||
      mensagem.tipo_direcao ||
      "",
  )
    .trim()
    .toLowerCase();

  if (
    [
      "recebida",
      "recebido",
      "in",
      "inbound",
      "incoming",
      "entrada",
      "cliente",
      "customer",
      "contato",
      "paciente",
      "lead",
    ].includes(direcao)
  ) {
    return false;
  }

  if (
    [
      "enviada",
      "enviado",
      "sent",
      "out",
      "outbound",
      "outgoing",
      "saida",
      "operador",
      "atendente",
      "usuario",
      "user",
      "agent",
      "operator",
      "captador",
    ].includes(direcao)
  ) {
    return true;
  }

  const remetente = String(
    mensagem.de ||
      mensagem.remetente ||
      mensagem.autor ||
      mensagem.sender ||
      mensagem.author ||
      "",
  )
    .trim()
    .toLowerCase();

  if (
    [
      "cliente",
      "contato",
      "paciente",
      "customer",
      "lead",
      "recebida",
      "recebido",
      "in",
      "inbound",
      "incoming",
    ].includes(remetente)
  ) {
    return false;
  }

  if (
    [
      "operador",
      "atendente",
      "usuario",
      "user",
      "agent",
      "operator",
      "captador",
      "enviada",
      "enviado",
      "sent",
      "out",
      "outbound",
      "outgoing",
    ].includes(remetente)
  ) {
    return true;
  }

  // Chamado fechado / supervisão / somente visualização:
  // nunca usa o usuário logado para decidir autoria. Nesses modos,
  // só considera operador quando há marca histórica da mensagem.
  const modoVisualizacao = Boolean(
    contexto?.isSupervisao ||
    contexto?.somenteVisualizacao ||
    contexto?.envioBloqueado ||
    contexto?.chamadoFechado,
  );

  if (modoVisualizacao) {
    return Boolean(
      mensagem.operador_id ||
      mensagem.operadorId ||
      mensagem.usuario_id ||
      mensagem.usuarioId ||
      mensagem.atendente_id ||
      mensagem.atendenteId ||
      mensagem.captador_id ||
      mensagem.captadorId ||
      mensagem.autor_nome ||
      mensagem.usuario_nome ||
      mensagem.operador_nome ||
      mensagem.atendente_nome ||
      mensagem.captador_nome,
    );
  }

  return false;
}

function isMensagemEmVisualizacaoSupervisao({
  mensagem = {},
  contato = {},
  modoVisualizacao = "",
} = {}) {
  const modo = String(
    modoVisualizacao ||
      contato?.modoVisualizacao ||
      contato?.modo_visualizacao ||
      "",
  )
    .trim()
    .toLowerCase();

  return Boolean(
    modo === "supervisao" ||
    mensagem?._supervisao_enviada_por_outro_operador ||
    contato?._modo_supervisao_visualizacao ||
    contato?._visualizacao_supervisao ||
    contato?._captazap_visualizacao_supervisao ||
    contato?._somente_visualizacao_supervisao ||
    contato?.somente_visualizacao_supervisao ||
    contato?._somente_visualizacao ||
    contato?.somente_visualizacao ||
    contato?.origem_visualizacao === "supervisao" ||
    contato?.origemVisualizacao === "supervisao",
  );
}

function isMensagemEmModoSomenteVisualizacao({
  mensagem = {},
  contato = {},
  modoVisualizacao = "",
} = {}) {
  const modo = String(
    modoVisualizacao ||
      contato?.modoVisualizacao ||
      contato?.modo_visualizacao ||
      contato?.origem_visualizacao ||
      contato?.origemVisualizacao ||
      "",
  )
    .trim()
    .toLowerCase();

  const status = String(
    contato?.status_chamado ||
      contato?.chamado_status ||
      contato?.statusAtendimento ||
      contato?.status_atendimento ||
      contato?.status ||
      contato?.aba_captazap ||
      "",
  )
    .trim()
    .toLowerCase();

  return Boolean(
    isMensagemEmVisualizacaoSupervisao({
      mensagem,
      contato,
      modoVisualizacao,
    }) ||
    modo === "visualizacao" ||
    modo === "visualização" ||
    modo === "somente_visualizacao" ||
    modo === "somente-visualizacao" ||
    modo === "fechado" ||
    contato?._somente_visualizacao ||
    contato?.somente_visualizacao ||
    contato?._captazap_somente_visualizacao ||
    contato?._visualizacao_chamado_fechado ||
    contato?._chamado_fechado_visualizacao ||
    contato?.envio_bloqueado ||
    contato?.bloqueado_envio ||
    contato?.envioBloqueado ||
    contato?.inputDisabled ||
    contato?.disabled ||
    contato?.fechado === true ||
    contato?.chamado_fechado === true ||
    contato?.chamadoFechado === true ||
    status === "fechado" ||
    status === "fechada" ||
    status === "encerrado" ||
    status === "encerrada" ||
    status.includes("fechado") ||
    status.includes("fechada") ||
    status.includes("encerrado") ||
    status.includes("encerrada"),
  );
}

function getNomeOperadorSupervisao({
  mensagem = {},
  contato = {},
  operador = {},
  ownerUserName = "",
} = {}) {
  return getPrimeiroValorTextoMensagem([
    mensagem?._supervisao_remetente_nome,
    mensagem?.autor_nome,
    mensagem?.usuario_nome,
    mensagem?.enviado_por_nome,
    mensagem?.operador_nome,
    mensagem?.atendente_nome,
    mensagem?.captador_nome,
    operador?.nome,
    operador?.nome_usuario,
    operador?.nome_completo,
    ownerUserName,
    contato?.responsavel_nome,
    contato?.captador_nome,
    contato?.usuario_responsavel_nome,
    contato?.atendente_nome,
    contato?.operador_nome,
    "Captador",
  ]);
}

function getNomeOperadorHistorico({
  mensagem = {},
  contato = {},
  ownerUserName = "",
} = {}) {
  const autorPersistido = getValorTextoOpcionalMensagem([
    mensagem?._supervisao_remetente_nome,
    mensagem?.autor_nome,
    mensagem?.usuario_nome,
    mensagem?.enviado_por_nome,
    mensagem?.operador_nome,
    mensagem?.atendente_nome,
  ]);

  if (autorPersistido) return autorPersistido;

  const responsavelReferencia = getValorTextoOpcionalMensagem([
    ownerUserName,
    contato?.responsavel_nome,
    contato?.captador_nome,
    contato?.usuario_responsavel_nome,
    contato?.atendente_nome,
    contato?.operador_nome,
  ]);

  return responsavelReferencia
    ? `Responsável pelo atendimento: ${responsavelReferencia}`
    : "Responsável pelo atendimento";
}

function getValorTextoOpcionalMensagem(values = []) {
  for (const value of values) {
    const text = String(value ?? "").trim();
    const normalizado = text.toLowerCase();

    if (!text || text === "0") continue;
    if (["null", "undefined", "você", "voce"].includes(normalizado)) continue;

    return text;
  }

  return "";
}

function getPrimeiroValorTextoMensagem(values = []) {
  for (const value of values) {
    const text = String(value ?? "").trim();

    if (!text) continue;
    if (text === "0") continue;
    if (text.toLowerCase() === "null") continue;
    if (text.toLowerCase() === "undefined") continue;
    if (text.toLowerCase() === "você" || text.toLowerCase() === "voce")
      continue;

    return text;
  }

  return "Captador";
}

function MessageStatus({ status, isOperador }) {
  if (!status || !isOperador) return null;

  const normalizedStatus = String(status).toLowerCase();

  const statusConfig = {
    pendente: {
      icon: <FaClock />,
      title: "Pendente",
      className: "text-slate-400",
    },
    enviando: {
      icon: <FaClock />,
      title: "Enviando",
      className: "text-slate-400",
    },
    enviada: {
      icon: <FaCheck />,
      title: "Enviada",
      className: "text-slate-400",
    },
    enviado: {
      icon: <FaCheck />,
      title: "Aceita pela Z-API",
      className: "text-slate-400",
    },
    sent: {
      icon: <FaCheck />,
      title: "Enviada",
      className: "text-slate-400",
    },
    entregue: {
      icon: <FaCheckDouble />,
      title: "Entregue",
      className: "text-slate-400",
    },
    recebida: {
      icon: <FaCheckDouble />,
      title: "Recebida",
      className: "text-slate-400",
    },
    recebido: {
      icon: <FaCheckDouble />,
      title: "Entregue",
      className: "text-slate-400",
    },
    received: {
      icon: <FaCheckDouble />,
      title: "Entregue",
      className: "text-slate-400",
    },
    lida: {
      icon: <FaCheckDouble />,
      title: "Lida",
      className: "text-sky-500",
    },
    read: {
      icon: <FaCheckDouble />,
      title: "Lida",
      className: "text-sky-500",
    },
    read_by_me: {
      icon: <FaCheckDouble />,
      title: "Lida",
      className: "text-sky-500",
    },
    played: {
      icon: <FaCheckDouble />,
      title: "Reproduzida",
      className: "text-sky-500",
    },
    reproduzido: {
      icon: <FaCheckDouble />,
      title: "Reproduzida",
      className: "text-sky-500",
    },
    reproduzida: {
      icon: <FaCheckDouble />,
      title: "Reproduzida",
      className: "text-sky-500",
    },
    editada: {
      icon: <FaPen />,
      title: "Editada",
      className: "text-slate-400",
    },
    apagada: {
      icon: <FaTrash />,
      title: "Apagada",
      className: "text-slate-400",
    },
    erro: {
      icon: <FaExclamationCircle />,
      title: "Erro no envio",
      className: "text-red-500",
    },
    falha: {
      icon: <FaExclamationCircle />,
      title: "Falha no envio",
      className: "text-red-500",
    },
    failed: {
      icon: <FaExclamationCircle />,
      title: "Falha no envio",
      className: "text-red-500",
    },
  };

  const config = statusConfig[normalizedStatus];

  if (!config) return null;

  return (
    <span
      title={config.title}
      className={`inline-flex items-center text-[11px] ${config.className}`}
    >
      {config.icon}
    </span>
  );
}

export default function MessageBubble({
  mensagem,
  contato,
  empresaId,
  onReply,
  operador,
  modoVisualizacao = "",
  viewerUserId = null,
  ownerUserId = null,
  ownerUserName = "",
}) {
  const [mensagemAtual, setMensagemAtual] = useState(mensagem || null);
  const [menuAberto, setMenuAberto] = useState(false);
  const [editando, setEditando] = useState(false);
  const [textoEdicao, setTextoEdicao] = useState("");
  const [salvandoAcao, setSalvandoAcao] = useState(false);
  const [erroAcao, setErroAcao] = useState("");

  useEffect(() => {
    setMensagemAtual(mensagem || null);
    setMenuAberto(false);
    setEditando(false);
    setTextoEdicao("");
    setErroAcao("");
  }, [
    mensagem?.id,
    mensagem?.msgid,
    mensagem?.texto,
    mensagem?.mensagem,
    mensagem?.body,
    mensagem?.message,
    mensagem?.status,
    mensagem?.midia_url,
  ]);

  if (!mensagemAtual) return null;

  mensagem = mensagemAtual;

  const mensagemEmpresaId = mensagem.empresa_id || mensagem.empresaId;

  const pertenceEmpresa =
    !empresaId ||
    !mensagemEmpresaId ||
    String(mensagemEmpresaId) === String(empresaId);

  if (!pertenceEmpresa) {
    return (
      <MensagemBloqueada
        empresaId={empresaId}
        mensagemEmpresaId={mensagemEmpresaId}
      />
    );
  }

  const tipoRemetente = String(
    mensagem.de ||
      mensagem.remetente ||
      mensagem.autor ||
      mensagem.direcao ||
      "",
  ).toLowerCase();

  const isSistema = [
    "sistema",
    "system",
    "bot",
    "automacao",
    "interno",
  ].includes(tipoRemetente);

  const midiaSistemaUrl = getMensagemMediaUrl(mensagem);
  const tipoMidiaSistema = normalizarTipoMidiaMensagem(mensagem);
  const direcaoMensagem = String(
    mensagem.direcao || mensagem.direction || "",
  ).toLowerCase();
  const contatoComDraMaluAtiva = Boolean(
    contato?._dra_malu_em_recuperacao ||
      contato?.dra_malu_em_recuperacao ||
      contato?._dra_malu_virtual_followup,
  );
  const isDraMalu = Boolean(
    tipoRemetente.includes("dra malu") ||
      tipoRemetente.includes("dra. malu") ||
      tipoRemetente.includes("dra_malu") ||
      mensagem?.dra_malu_atendimento_id ||
      mensagem?._dra_malu ||
      (contatoComDraMaluAtiva &&
        isSistema &&
        direcaoMensagem === "enviada" &&
        midiaSistemaUrl &&
        tipoMidiaSistema),
  );

  const isSupervisao = isMensagemEmVisualizacaoSupervisao({
    mensagem,
    contato,
    modoVisualizacao,
  });

  const isSomenteVisualizacaoHistorica = isMensagemEmModoSomenteVisualizacao({
    mensagem,
    contato,
    modoVisualizacao,
  });

  // Regra defensiva para não jogar mídia recebida para o lado do operador.
  // Prioridade: flags explícitas > direção > remetente.
  // Em supervisão/chamado fechado, não usa o usuário logado para decidir autoria.
  const isOperador = isMensagemDoOperador(mensagem, {
    isSupervisao,
    somenteVisualizacao: isSomenteVisualizacaoHistorica,
    envioBloqueado:
      contato?.envio_bloqueado ||
      contato?.bloqueado_envio ||
      contato?.envioBloqueado,
    chamadoFechado:
      contato?.status_chamado === "fechado" ||
      contato?.chamado_status === "fechado" ||
      contato?.fechado === true ||
      contato?.chamado_fechado === true ||
      contato?.chamadoFechado === true,
  });

  // Mensagens internas sem mídia continuam no formato de sistema. Já uma mídia
  // enviada por automação precisa usar a bolha normal para o player aparecer.
  // Isso corrige os áudios antigos da Dra. Malu gravados como autor="sistema".
  if (isSistema && !isDraMalu && !midiaSistemaUrl) {
    return <MensagemSistema mensagem={mensagem} />;
  }

  const canal = contato?.canal || mensagem.canal || "whatsapp";
  const canalStyle = getCanalStyle(canal);

  const nomeContato =
    contato?.nome ||
    contato?.nome_lead ||
    contato?.lead?.nome_lead ||
    contato?.lead?.nome ||
    contato?.numero ||
    contato?.telefone ||
    "Contato";

  const autorUsuarioId =
    mensagem?.usuario_id ||
    mensagem?.usuarioId ||
    mensagem?.operador_id ||
    mensagem?.operadorId ||
    mensagem?.atendente_id ||
    mensagem?.atendenteId ||
    null;

  const autorEhVisualizador = Boolean(
    autorUsuarioId &&
      viewerUserId &&
      String(autorUsuarioId) === String(viewerUserId),
  );

  const autorUsuarioNome = getValorTextoOpcionalMensagem([
    mensagem?.autor_nome,
    mensagem?.usuario_nome,
    mensagem?.enviado_por_nome,
    mensagem?.operador_nome,
    mensagem?.atendente_nome,
  ]);
  const temAutoriaOperadorPersistida = Boolean(
    autorUsuarioId || autorUsuarioNome,
  );

  const nomeOperadorReal = isDraMalu
    ? "Dra. Malu IA"
    : isSistema
      ? "Automação"
      : !temAutoriaOperadorPersistida
        ? getNomeOperadorHistorico({ mensagem, contato, ownerUserName })
        : operador?.nome ||
      operador?.nome_usuario ||
      operador?.nome_completo ||
      mensagem?.autor_nome ||
      mensagem?.usuario_nome ||
      mensagem?.operador_nome ||
      mensagem?.atendente_nome ||
      autorUsuarioNome ||
      "Atendente";

  const nomeOperador = autorEhVisualizador ? "Você" : nomeOperadorReal;

  const nomeRemetente = isOperador ? nomeOperador : nomeContato;

  const hora =
    mensagem.hora ||
    mensagem.created_at ||
    mensagem.createdAt ||
    mensagem.timestamp ||
    mensagem.datahora;

  const texto =
    mensagem.texto ||
    mensagem.body ||
    mensagem.mensagem ||
    mensagem.message ||
    "";

  const midiaUrl = getMensagemMediaUrl(mensagem);

  const mensagemApagada = isMensagemApagada(mensagem);
  const mensagemEditada = isMensagemEditada(mensagem);

  const erro = mensagem.erro || mensagem.error || mensagem.errorMessage;

  const telefoneContato =
    contato?.telefone ||
    contato?.numero ||
    mensagem?.telefone ||
    mensagem?.phone ||
    mensagem?.numero ||
    "";

  const contatoFoto = getImagemUrl(
    contato?.foto ||
      contato?.avatar ||
      contato?.avatar_url ||
      contato?.foto_url ||
      contato?.foto_perfil ||
      contato?.profileUrl ||
      mensagem?.avatar_cliente ||
      mensagem?.cliente_avatar ||
      mensagem?.profileUrl ||
      "",
  );

  const operadorFoto = isDraMalu || isSistema
    ? ""
    : getImagemUrl(
    operador?.foto_original ||
      operador?.foto_url_original ||
      operador?.foto_perfil ||
      operador?.fotoPerfil ||
      operador?.foto ||
      operador?.foto_url ||
      operador?.avatar ||
      operador?.avatar_url ||
      operador?.imagem ||
      operador?.imagem_url ||
      operador?.perfil_foto ||
      mensagem?.operador_foto ||
      mensagem?.avatar_operador ||
      mensagem?.usuario_foto ||
      mensagem?.captador_foto ||
      "",
  );

  const mensagemId =
    mensagem.id || mensagem.whatsapp_id || mensagem.message_db_id;
  const mensagemMsgId =
    mensagem.msgid ||
    mensagem.messageId ||
    mensagem.messageID ||
    mensagem.wa_message_id;

  const podeResponder = Boolean(
    !isSomenteVisualizacaoHistorica &&
      mensagemMsgId &&
      !mensagemApagada &&
      typeof onReply === "function",
  );

  const textoEditavel = String(texto || "").trim();
  const tipoMidia = String(mensagem.tipo_midia || mensagem.tipo || "").trim();

  const podeEditar = Boolean(
    !isSomenteVisualizacaoHistorica &&
    isOperador &&
    mensagemId &&
    mensagemMsgId &&
    textoEditavel &&
    !midiaUrl &&
    !tipoMidia &&
    !mensagemApagada,
  );

  const podeApagar = Boolean(
    !isSomenteVisualizacaoHistorica &&
    isOperador &&
    mensagemId &&
    mensagemMsgId &&
    !mensagemApagada,
  );

  const abrirEdicao = () => {
    setTextoEdicao(textoEditavel);
    setEditando(true);
    setMenuAberto(false);
    setErroAcao("");
  };

  const responderMensagem = () => {
    if (!podeResponder) return;

    onReply({
      messageId: String(mensagemMsgId),
      texto: getResumoMensagemCitada(mensagem),
      tipo: normalizarTipoMidiaMensagem(mensagem) || "texto",
      autor: isOperador ? nomeOperador : nomeContato,
      isOperador,
    });
    setMenuAberto(false);
  };

  const confirmarEdicao = async () => {
    const novoTexto = String(textoEdicao || "").trim();

    if (!novoTexto || novoTexto === textoEditavel || salvandoAcao) {
      if (!novoTexto) setErroAcao("Digite o novo texto da mensagem.");
      else setEditando(false);
      return;
    }

    try {
      setSalvandoAcao(true);
      setErroAcao("");

      const data = await requestJsonMessage(
        `/whatsapp/mensagens/${encodeURIComponent(mensagemId)}/editar`,
        {
          method: "PUT",
          body: JSON.stringify({
            empresa_id: empresaId || mensagemEmpresaId,
            texto: novoTexto,
          }),
        },
      );

      const atualizada = data?.mensagem || data?.data || data || {};

      setMensagemAtual((atual) => ({
        ...atual,
        ...atualizada,
        texto: atualizada.texto ?? novoTexto,
        mensagem: atualizada.mensagem ?? novoTexto,
        status: atualizada.status || "editada",
      }));

      setEditando(false);
      setTextoEdicao("");
    } catch (error) {
      setErroAcao(error?.message || "Não foi possível editar a mensagem.");
    } finally {
      setSalvandoAcao(false);
    }
  };

  const confirmarApagar = async () => {
    if (!podeApagar || salvandoAcao) return;

    const confirmar = window.confirm(
      "Apagar esta mensagem no WhatsApp e marcar como apagada no CaptaZap?",
    );

    if (!confirmar) return;

    try {
      setSalvandoAcao(true);
      setErroAcao("");
      setMenuAberto(false);

      const params = new URLSearchParams();
      params.set("empresa_id", String(empresaId || mensagemEmpresaId || ""));

      const data = await requestJsonMessage(
        `/whatsapp/mensagens/${encodeURIComponent(mensagemId)}?${params.toString()}`,
        { method: "DELETE" },
      );

      const apagada = data?.mensagem || data?.data || data || {};

      setMensagemAtual((atual) => ({
        ...atual,
        ...apagada,
        texto: apagada.texto ?? "",
        mensagem: apagada.mensagem ?? "",
        body: apagada.body ?? "",
        message: apagada.message ?? "",
        midia_url: apagada.midia_url ?? "",
        mediaUrl: apagada.mediaUrl ?? "",
        status: apagada.status || "apagada",
      }));
    } catch (error) {
      setErroAcao(error?.message || "Não foi possível apagar a mensagem.");
    } finally {
      setSalvandoAcao(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 6, scale: 0.99 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.16, ease: "easeOut" }}
      className={`flex w-full ${isOperador ? "justify-end" : "justify-start"}`}
    >
      <div
        className={`flex max-w-[76%] gap-2 ${
          isOperador ? "flex-row-reverse" : "flex-row"
        }`}
      >
        <AvatarMensagem
          foto={isOperador ? operadorFoto : contatoFoto}
          canal={canal}
          canalStyle={canalStyle}
          isOperador={isOperador}
          empresaId={empresaId}
          telefone={telefoneContato}
          isDraMalu={isDraMalu}
        />

        <div className="min-w-0">
          <div
            className={`mb-1 flex items-center gap-2 px-1 text-[11px] ${
              isOperador
                ? "justify-end text-slate-400"
                : "justify-start text-slate-500"
            }`}
          >
            <span className="font-semibold">{nomeRemetente}</span>
            {isDraMalu ? (
              <span className="rounded-full border border-violet-200 bg-violet-50 px-1.5 py-0.5 text-[8px] font-black uppercase tracking-wide text-violet-700">
                IA
              </span>
            ) : null}
            {hora && <span>{formatHora(hora)}</span>}
          </div>

          <div
            className={`relative ${menuAberto ? "z-40" : "z-0"} overflow-visible rounded-[22px] px-3 py-2 text-[13px] leading-5 shadow-sm ring-1 ${
              isOperador
                ? "rounded-tr-md bg-white text-slate-800 ring-blue-200"
                : "rounded-tl-md bg-white text-slate-800 ring-slate-200"
            }`}
          >
            <div
              className={`absolute bottom-0 top-0 w-1 ${
                isOperador
                  ? "right-0 bg-blue-500"
                  : `left-0 ${canalStyle.clientAccent}`
              }`}
            />

            {(podeResponder || podeEditar || podeApagar) && (
              <div className="absolute right-2 top-1.5 z-[90]">
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    setMenuAberto((aberto) => !aberto);
                    setErroAcao("");
                  }}
                  disabled={salvandoAcao}
                  className="flex h-7 w-7 items-center justify-center rounded-full bg-white/80 text-slate-400 shadow-sm ring-1 ring-slate-200 transition hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
                  title="Opções da mensagem"
                >
                  <FaEllipsisV size={12} />
                </button>

                {menuAberto && (
                  <div
                    className="absolute right-0 top-8 z-[100] min-w-[190px] overflow-hidden whitespace-nowrap rounded-2xl border border-slate-200 bg-white py-1 text-xs font-bold text-slate-700 shadow-[0_18px_45px_rgba(15,23,42,0.22)]"
                    onClick={(event) => event.stopPropagation()}
                  >
                    {podeResponder && (
                      <button
                        type="button"
                        onClick={responderMensagem}
                        className="flex w-full items-center gap-2 px-3 py-2 text-left transition hover:bg-blue-50 hover:text-blue-700"
                      >
                        <FaReply size={11} />
                        Responder mensagem
                      </button>
                    )}

                    {podeEditar && (
                      <button
                        type="button"
                        onClick={abrirEdicao}
                        className="flex w-full items-center gap-2 px-3 py-2 text-left transition hover:bg-blue-50 hover:text-blue-700"
                      >
                        <FaPen size={11} />
                        Editar mensagem
                      </button>
                    )}

                    {podeApagar && (
                      <button
                        type="button"
                        onClick={confirmarApagar}
                        className="flex w-full items-center gap-2 px-3 py-2 text-left text-red-600 transition hover:bg-red-50"
                      >
                        <FaTrash size={11} />
                        Apagar mensagem
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className={isOperador ? "pr-8" : "pl-1"}>
              <MensagemCitada mensagem={mensagem} />

              {mensagemApagada ? (
                <div className="inline-flex items-center gap-2 rounded-xl bg-slate-50 px-2.5 py-1.5 text-xs font-semibold italic text-slate-400 ring-1 ring-slate-200">
                  <FaTrash size={11} />
                  Mensagem apagada
                </div>
              ) : (
                <>
                  {texto && (
                    <div className="whitespace-pre-wrap break-words">
                      {renderTextoComLinks(texto)}
                      {mensagemEditada && (
                        <span className="ml-2 align-baseline text-[10px] font-semibold italic text-slate-400">
                          editada
                        </span>
                      )}
                    </div>
                  )}

                  {midiaUrl && (
                    <MediaPreview
                      mensagem={{
                        ...mensagem,
                        midia_url: midiaUrl,
                      }}
                      empresaId={empresaId}
                      isOperador={isOperador}
                      canalStyle={canalStyle}
                    />
                  )}

                  {!texto && !midiaUrl && (
                    <div className="text-slate-400">Mensagem sem conteúdo.</div>
                  )}
                </>
              )}

              <div className="mt-1 flex items-center justify-end gap-1.5">
                {hora && (
                  <span className="text-[10px] font-semibold text-slate-400">
                    {formatHora(hora)}
                  </span>
                )}

                <MessageStatus
                  status={mensagem.status}
                  isOperador={isOperador}
                />
              </div>
            </div>
          </div>

          {erro && (
            <div className="mt-1 flex items-center gap-1 px-1 text-[11px] font-medium text-red-500">
              <FaExclamationCircle size={11} />
              <span>{erro}</span>
            </div>
          )}

          {erroAcao && (
            <div className="mt-1 flex items-center gap-1 px-1 text-[11px] font-medium text-red-500">
              <FaExclamationCircle size={11} />
              <span>{erroAcao}</span>
            </div>
          )}
        </div>
      </div>

      {editando && (
        <EditarMensagemModal
          texto={textoEdicao}
          salvando={salvandoAcao}
          erro={erroAcao}
          onChange={setTextoEdicao}
          onCancel={() => {
            setEditando(false);
            setTextoEdicao("");
            setErroAcao("");
          }}
          onConfirm={confirmarEdicao}
        />
      )}
    </motion.div>
  );
}

function EditarMensagemModal({
  texto,
  salvando = false,
  erro = "",
  onChange,
  onCancel,
  onConfirm,
}) {
  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-950/45 px-4 py-6 backdrop-blur-sm">
      <div className="w-full max-w-lg overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-[0_30px_90px_rgba(15,23,42,0.28)]">
        <div className="flex items-start justify-between gap-4 border-b border-slate-200 bg-slate-50 px-5 py-4">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1 text-[11px] font-black uppercase tracking-wide text-blue-700 ring-1 ring-blue-200">
              <FaPen size={12} />
              Editar mensagem
            </div>
            <p className="mt-2 text-xs font-semibold leading-5 text-slate-500">
              A edição será enviada para o WhatsApp usando o ID original da
              mensagem.
            </p>
          </div>

          <button
            type="button"
            onClick={onCancel}
            disabled={salvando}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-500 shadow-sm transition hover:border-red-200 hover:bg-red-50 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <FaTimes size={13} />
          </button>
        </div>

        <div className="px-5 py-4">
          <textarea
            value={texto}
            onChange={(event) => onChange?.(event.target.value)}
            rows={5}
            autoFocus
            disabled={salvando}
            className="w-full resize-none rounded-2xl border border-slate-300 bg-white px-3 py-3 text-sm font-medium leading-6 text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 disabled:cursor-not-allowed disabled:bg-slate-50"
            placeholder="Digite a nova mensagem..."
          />

          {erro ? (
            <div className="mt-2 flex items-center gap-1.5 text-xs font-semibold text-red-500">
              <FaExclamationCircle size={12} />
              {erro}
            </div>
          ) : null}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-slate-200 bg-slate-50 px-5 py-4">
          <button
            type="button"
            onClick={onCancel}
            disabled={salvando}
            className="h-10 rounded-2xl border border-slate-300 bg-white px-4 text-xs font-black text-slate-600 shadow-sm transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
          >
            Cancelar
          </button>

          <button
            type="button"
            onClick={onConfirm}
            disabled={salvando}
            className="inline-flex h-10 items-center justify-center gap-2 rounded-2xl bg-blue-700 px-4 text-xs font-black text-white shadow-sm transition hover:-translate-y-0.5 hover:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <FaPen size={12} />
            {salvando ? "Salvando..." : "Salvar edição"}
          </button>
        </div>
      </div>
    </div>
  );
}

function AvatarMensagem({
  foto,
  canal,
  canalStyle,
  isOperador,
  empresaId,
  telefone,
  isDraMalu = false,
}) {
  const [erroImagem, setErroImagem] = useState(false);
  const [avatarContato, setAvatarContato] = useState("");
  const fotoFinal = getImagemUrl(foto || avatarContato);

  useEffect(() => {
    let cancelado = false;

    async function carregarAvatarContato() {
      if (isOperador || foto || !empresaId || !telefone) return;

      try {
        const profileUrl = await buscarAvatarContato({ empresaId, telefone });

        if (!cancelado && profileUrl) {
          setAvatarContato(profileUrl);
          setErroImagem(false);
        }
      } catch {
        // mantém fallback sem quebrar a conversa
      }
    }

    carregarAvatarContato();

    return () => {
      cancelado = true;
    };
  }, [empresaId, foto, isOperador, telefone]);

  return (
    <div className="relative mt-1 shrink-0">
      {isDraMalu ? (
        <img
          src={DRA_MALU_AVATAR_URL}
          alt="Dra. Malu IA"
          title="Dra. Malu IA"
          loading="eager"
          decoding="async"
          className="h-8 w-8 rounded-full object-cover object-center shadow-sm ring-2 ring-violet-200"
        />
      ) : fotoFinal && !erroImagem ? (
        <img
          src={fotoFinal}
          alt={isOperador ? "Captador" : "Contato"}
          loading="eager"
          decoding="async"
          onError={() => setErroImagem(true)}
          className={`h-8 w-8 rounded-full object-cover object-center shadow-sm ring-2 ${
            isOperador ? "ring-blue-200" : canalStyle.avatarRing
          }`}
        />
      ) : (
        <div
          className={`flex h-8 w-8 items-center justify-center rounded-full ring-2 ${
            isOperador
              ? "bg-blue-50 text-blue-600 ring-blue-200"
              : canalStyle.avatarBg
          }`}
        >
          <FaUserCircle size={20} />
        </div>
      )}

      <span
        className={`absolute -bottom-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full border-2 border-white shadow-sm ${
          isOperador ? "bg-blue-50" : canalStyle.iconBg
        }`}
      >
        {isDraMalu ? (
          <span className="text-[7px] font-black text-violet-700">IA</span>
        ) : isOperador ? (
          <FaCheck className="text-blue-500" size={8} />
        ) : (
          <CanalIcon canal={canal} size={9} />
        )}
      </span>
    </div>
  );
}

function getResumoMensagemCitada(mensagem = {}) {
  const texto = String(
    mensagem.texto ||
      mensagem.body ||
      mensagem.mensagem ||
      mensagem.message ||
      "",
  ).trim();

  if (texto) return texto.slice(0, 500);

  const tipo = normalizarTipoMidiaMensagem(mensagem);
  const rotulos = {
    sticker: "🧩 Figurinha",
    imagem: "📷 Imagem",
    video: "🎥 Vídeo",
    audio: "🎤 Áudio",
    documento: "📄 Documento",
  };

  return rotulos[tipo] || "Mensagem";
}

function getDadosMensagemCitada(mensagem = {}) {
  const fonte =
    mensagem.resposta ||
    mensagem.reply ||
    mensagem.citacao ||
    mensagem.quotedMessage ||
    mensagem.quoted_message ||
    {};

  const messageId =
    mensagem.resposta_msgid ||
    mensagem.reply_message_id ||
    mensagem.quoted_message_id ||
    fonte.messageId ||
    fonte.msgid ||
    fonte.id ||
    "";

  const texto = String(
    mensagem.resposta_texto ||
      mensagem.reply_text ||
      mensagem.quoted_text ||
      fonte.texto ||
      fonte.text ||
      fonte.message ||
      fonte.body ||
      "",
  ).trim();

  const tipo = String(
    mensagem.resposta_tipo ||
      mensagem.reply_type ||
      mensagem.quoted_type ||
      fonte.tipo ||
      fonte.type ||
      "",
  ).trim();

  const autor = String(
    mensagem.resposta_autor ||
      mensagem.reply_author ||
      mensagem.quoted_author ||
      fonte.autor ||
      fonte.author ||
      "",
  ).trim();

  if (!messageId && !texto && !tipo) return null;

  return {
    texto: texto || getRotuloTipoMensagemCitada(tipo),
    autor,
  };
}

function getRotuloTipoMensagemCitada(tipoRaw) {
  const tipo = String(tipoRaw || "").toLowerCase();
  if (/sticker|figurinha/.test(tipo)) return "🧩 Figurinha";
  if (/image|imagem|photo|foto/.test(tipo)) return "📷 Imagem";
  if (/video/.test(tipo)) return "🎥 Vídeo";
  if (/audio|ptt|voice/.test(tipo)) return "🎤 Áudio";
  if (/document|documento|pdf|file/.test(tipo)) return "📄 Documento";
  return "Mensagem citada";
}

function MensagemCitada({ mensagem }) {
  const citada = getDadosMensagemCitada(mensagem);
  if (!citada) return null;

  return (
    <div className="mb-2 overflow-hidden rounded-xl border border-slate-200 bg-slate-50/90">
      <div className="border-l-4 border-blue-500 px-2.5 py-1.5">
        {citada.autor ? (
          <div className="truncate text-[10px] font-black text-blue-700">
            {citada.autor}
          </div>
        ) : null}
        <div className="line-clamp-2 whitespace-pre-wrap break-words text-[11px] leading-4 text-slate-600">
          {citada.texto}
        </div>
      </div>
    </div>
  );
}

function MediaPreview({ mensagem, empresaId, isOperador, canalStyle }) {
  const [imagemAmpliada, setImagemAmpliada] = useState(false);
  const [salvandoFigurinha, setSalvandoFigurinha] = useState(false);
  const [figurinhaSalva, setFigurinhaSalva] = useState(false);
  const [erroFigurinha, setErroFigurinha] = useState("");
  const [baixando, setBaixando] = useState(false);
  const [erroImagem, setErroImagem] = useState(false);
  const [erroDownload, setErroDownload] = useState("");
  const tipo = normalizarTipoMidiaMensagem(mensagem);
  const midiaUrl = getMensagemMediaUrl(mensagem);

  if (!midiaUrl) return null;

  const fileName =
    mensagem.nome_arquivo ||
    mensagem.fileName ||
    mensagem.filename ||
    mensagem.name ||
    mensagem.document?.fileName ||
    mensagem.document?.filename ||
    mensagem.documento?.nome_arquivo ||
    (tipo === "documento" ? mensagem.texto : "") ||
    "Abrir anexo";

  const baixarArquivo = async () => {
    if (!midiaUrl || baixando) return;

    setBaixando(true);
    setErroDownload("");

    try {
      const response = await fetch(midiaUrl, {
        method: "GET",
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const blob = await response.blob();
      if (!blob.size) throw new Error("Arquivo vazio");

      const contentType = blob.type || mensagem.mime_type || "";
      const extension = extensaoMidia(contentType, midiaUrl);
      const baseName =
        String(fileName || "imagem-recebida")
          .replace(/\.[a-z0-9]{1,10}$/i, "")
          .replace(/[^a-z0-9_-]/gi, "_") || "imagem-recebida";
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = objectUrl;
      link.download = `${baseName}.${extension}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    } catch (error) {
      console.error("Falha ao baixar mídia:", error);
      setErroDownload(
        tipo === "documento"
          ? "Não foi possível baixar este documento."
          : "Não foi possível baixar esta imagem.",
      );
    } finally {
      setBaixando(false);
    }
  };

  const attachmentClass = `mt-2 flex items-center gap-2 rounded-xl px-2.5 py-2 text-sm ring-1 transition ${
    isOperador
      ? "bg-blue-50 text-blue-700 ring-blue-200 hover:bg-blue-100"
      : canalStyle.clientAttachment
  }`;

  const ehFigurinha = ["sticker", "figurinha"].includes(tipo);
  const mensagemId = Number(
    mensagem?.id || mensagem?.whatsapp_id || mensagem?.message_db_id || 0,
  ) || null;

  const salvarFigurinhaNaEmpresa = async () => {
    if (!empresaId || !mensagemId || salvandoFigurinha || figurinhaSalva) return;

    try {
      setSalvandoFigurinha(true);
      setErroFigurinha("");
      await requestJsonMessage("/whatsapp/captazap/figurinhas/salvar", {
        method: "POST",
        body: JSON.stringify({
          empresa_id: empresaId,
          mensagem_id: mensagemId,
        }),
      });
      setFigurinhaSalva(true);
    } catch (error) {
      setErroFigurinha(
        error?.message || "Não foi possível salvar esta figurinha.",
      );
    } finally {
      setSalvandoFigurinha(false);
    }
  };

  if (ehFigurinha) {
    return (
      <div className="mt-2">
        <div className="inline-flex min-h-[150px] min-w-[150px] items-center justify-center rounded-2xl bg-transparent p-1">
          {!erroImagem ? (
            <img
              src={midiaUrl}
              alt="Figurinha recebida"
              loading="lazy"
              decoding="async"
              onError={() => setErroImagem(true)}
              className="max-h-44 max-w-[190px] object-contain"
            />
          ) : (
            <div className="flex h-32 w-40 flex-col items-center justify-center gap-2 rounded-2xl bg-slate-50 text-center text-xs font-semibold text-slate-500 ring-1 ring-slate-200">
              <FaExclamationCircle className="text-red-500" />
              Figurinha indisponível
            </div>
          )}
        </div>

        {!isOperador && mensagemId ? (
          <div className="mt-1">
            <button
              type="button"
              onClick={salvarFigurinhaNaEmpresa}
              disabled={salvandoFigurinha || figurinhaSalva || erroImagem}
              className={`rounded-xl border px-2.5 py-1 text-[10px] font-black transition disabled:cursor-not-allowed ${
                figurinhaSalva
                  ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                  : "border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 disabled:opacity-60"
              }`}
              title="Salvar esta figurinha na biblioteca da empresa"
            >
              {figurinhaSalva
                ? "✓ Salva na empresa"
                : salvandoFigurinha
                  ? "Salvando..."
                  : "Salvar na empresa"}
            </button>
            {erroFigurinha ? (
              <div className="mt-1 text-[10px] font-semibold text-red-600">
                {erroFigurinha}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    );
  }

  if (tipo === "imagem" || tipo === "image" || isImageUrl(midiaUrl)) {
    return (
      <>
        <div className="relative mt-2 overflow-hidden rounded-xl bg-slate-50 ring-1 ring-black/5">
          {!erroImagem ? (
            <button
              type="button"
              onClick={() => setImagemAmpliada(true)}
              className="block cursor-zoom-in"
              title="Ampliar imagem"
            >
              <img
                src={midiaUrl}
                alt={mensagem.legenda || mensagem.caption || "Imagem recebida"}
                loading="lazy"
                decoding="async"
                onError={() => setErroImagem(true)}
                className="max-h-72 w-full max-w-[320px] object-contain transition hover:scale-[1.01]"
              />
            </button>
          ) : (
            <div className="flex min-h-28 w-full max-w-[320px] flex-col items-center justify-center gap-2 px-4 py-5 text-center text-xs font-semibold text-slate-500">
              <FaExclamationCircle className="text-red-500" />
              Imagem indisponível
            </div>
          )}

          <div className="absolute right-2 top-2 flex gap-1">
            {!erroImagem && (
              <button
                type="button"
                onClick={() => setImagemAmpliada(true)}
                className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-900/70 text-white transition hover:bg-slate-900"
                title="Ampliar imagem"
              >
                <FaExpand size={12} />
              </button>
            )}
            <button
              type="button"
              onClick={baixarArquivo}
              disabled={baixando}
              className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-900/70 text-white transition hover:bg-slate-900 disabled:cursor-wait disabled:opacity-60"
              title={baixando ? "Baixando..." : "Baixar imagem"}
            >
              <FaDownload size={12} />
            </button>
          </div>
        </div>

        {erroDownload && (
          <div className="mt-1 text-[11px] font-semibold text-red-600">
            {erroDownload}
          </div>
        )}

        {imagemAmpliada && (
          <div
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 p-4"
            role="dialog"
            aria-modal="true"
            aria-label="Imagem ampliada"
            onClick={() => setImagemAmpliada(false)}
          >
            <button
              type="button"
              onClick={() => setImagemAmpliada(false)}
              className="absolute right-5 top-5 flex h-10 w-10 items-center justify-center rounded-full bg-white text-slate-800 shadow-lg"
              title="Fechar"
            >
              <FaTimes />
            </button>
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                baixarArquivo();
              }}
              disabled={baixando}
              className="absolute right-16 top-5 flex h-10 items-center gap-2 rounded-full bg-white px-4 text-sm font-bold text-slate-800 shadow-lg disabled:opacity-60"
              title="Baixar imagem"
            >
              <FaDownload />
              {baixando ? "Baixando..." : "Baixar"}
            </button>
            <img
              src={midiaUrl}
              alt={mensagem.legenda || mensagem.caption || "Imagem recebida"}
              className="max-h-[90vh] max-w-[92vw] rounded-lg object-contain shadow-2xl"
              onClick={(event) => event.stopPropagation()}
            />
          </div>
        )}
      </>
    );
  }

  if (["audio", "voice", "voz", "ptt"].includes(tipo)) {
    return (
      <div className="mt-2 rounded-xl bg-slate-50 p-2 ring-1 ring-black/5">
        <div className="mb-1 flex items-center gap-2 text-[11px] font-bold text-slate-500">
          <FaMicrophone size={11} />
          {isOperador ? "Áudio enviado" : "Áudio recebido"}
        </div>
        <audio
          src={midiaUrl}
          controls
          preload="metadata"
          className="w-full max-w-[320px]"
        />
        <a
          href={midiaUrl}
          target="_blank"
          rel="noreferrer"
          className="mt-1 inline-flex text-[11px] font-semibold text-blue-600 hover:text-blue-800"
        >
          Abrir áudio
        </a>
      </div>
    );
  }

  if (["video", "vídeo"].includes(tipo) || isVideoUrl(midiaUrl)) {
    return (
      <div className="mt-2 overflow-hidden rounded-xl bg-slate-50 p-2 ring-1 ring-black/5">
        <video
          src={midiaUrl}
          controls
          preload="metadata"
          className="max-h-72 w-full max-w-[360px] rounded-lg bg-black"
        />
        <a
          href={midiaUrl}
          target="_blank"
          rel="noreferrer"
          className="mt-1 inline-flex items-center gap-1 text-[11px] font-semibold text-blue-600 hover:text-blue-800"
        >
          <FaVideo size={10} />
          Abrir vídeo
        </a>
      </div>
    );
  }

  return (
    <>
      <div className={attachmentClass}>
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-white/60">
          <FaFileAlt />
        </span>

        <a
          href={midiaUrl}
          target="_blank"
          rel="noreferrer"
          className="min-w-0 flex-1"
          title={`Abrir ${fileName}`}
        >
          <span className="block truncate font-semibold">{fileName}</span>
          <span className="block text-xs opacity-70">Clique para abrir</span>
        </a>

        <button
          type="button"
          onClick={baixarArquivo}
          disabled={baixando}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white/70 transition hover:bg-white disabled:cursor-wait disabled:opacity-60"
          title={baixando ? "Baixando..." : "Baixar documento"}
        >
          <FaDownload size={12} />
        </button>
      </div>

      {erroDownload && (
        <div className="mt-1 text-[11px] font-semibold text-red-600">
          {erroDownload}
        </div>
      )}
    </>
  );
}

function getMensagemMediaUrl(mensagem = {}) {
  const candidatos = [
    mensagem.midia_url,
    mensagem.mediaUrl,
    mensagem.media_url,
    mensagem.url_midia,
    mensagem.arquivo_url,
    mensagem.fileUrl,
    mensagem.file_url,
    mensagem.url,
    mensagem.link,
    mensagem.stickerUrl,
    mensagem.sticker?.stickerUrl,
    mensagem.sticker?.url,
    mensagem.imageUrl,
    mensagem.videoUrl,
    mensagem.audioUrl,
    mensagem.documentUrl,
    mensagem.image?.imageUrl,
    mensagem.image?.url,
    mensagem.image?.link,
    mensagem.video?.videoUrl,
    mensagem.video?.url,
    mensagem.video?.link,
    mensagem.audio?.audioUrl,
    mensagem.audio?.url,
    mensagem.audio?.link,
    mensagem.document?.documentUrl,
    mensagem.document?.url,
    mensagem.document?.link,
    mensagem.documento?.url,
    mensagem.documento?.link,
    mensagem.midia?.url,
    mensagem.media?.url,
  ];

  for (const value of candidatos) {
    const url = String(value || "").trim();
    if (url) return getImagemUrl(url);
  }

  return "";
}

function extensaoMidia(mime = "", url = "") {
  const mimeLimpo = String(mime).split(";")[0].trim().toLowerCase();
  const porMime = {
    "image/jpeg": "jpg",
    "image/jpg": "jpg",
    "image/png": "png",
    "image/webp": "webp",
    "image/gif": "gif",
    "image/bmp": "bmp",
    "image/avif": "avif",
    "image/heic": "heic",
    "image/heif": "heif",
    "application/pdf": "pdf",
  };

  if (porMime[mimeLimpo]) return porMime[mimeLimpo];

  const match = String(url).match(
    /\.((?:jpe?g|png|webp|gif|bmp|avif|heic|heif|pdf))(?:[?#]|$)/i,
  );
  if (match?.[1]) {
    return match[1].toLowerCase() === "jpeg"
      ? "jpg"
      : match[1].toLowerCase();
  }

  return mimeLimpo === "application/pdf" ? "pdf" : "bin";
}

function normalizarTipoMidiaMensagem(mensagem = {}) {
  const raw = String(
    mensagem.tipo_midia ||
      mensagem.tipoMidia ||
      mensagem.mediaType ||
      mensagem.media_type ||
      mensagem.tipo ||
      mensagem.type ||
      "",
  )
    .trim()
    .toLowerCase();

  if (["sticker", "figurinha"].includes(raw)) return "sticker";
  if (["image", "imagem", "foto", "picture"].includes(raw)) return "imagem";
  if (["video", "vídeo"].includes(raw)) return "video";
  if (["audio", "áudio", "voice", "voz", "ptt"].includes(raw)) return "audio";
  if (["document", "documento", "file", "arquivo"].includes(raw))
    return "documento";

  const mime = String(
    mensagem.mime_type || mensagem.mimeType || mensagem.mimetype || "",
  ).toLowerCase();
  if (mime.startsWith("image/")) return "imagem";
  if (mime.startsWith("video/")) return "video";
  if (mime.startsWith("audio/")) return "audio";

  const url = getMensagemMediaUrl(mensagem);
  if (isImageUrl(url)) return "imagem";
  if (isVideoUrl(url)) return "video";
  if (isAudioUrl(url)) return "audio";

  return raw || "documento";
}

function renderTextoComLinks(texto = "") {
  const partes = String(texto || "").split(/(https?:\/\/[^\s]+|www\.[^\s]+)/gi);

  return partes.map((parte, index) => {
    if (!parte) return null;

    const isUrl = /^(https?:\/\/|www\.)/i.test(parte);
    if (!isUrl) return parte;

    const href = parte.startsWith("http") ? parte : `https://${parte}`;

    return (
      <a
        key={`${href}-${index}`}
        href={href}
        target="_blank"
        rel="noreferrer"
        className="font-semibold text-blue-600 underline decoration-blue-300 underline-offset-2 hover:text-blue-800"
      >
        {parte}
      </a>
    );
  });
}

function MensagemSistema({ mensagem }) {
  const texto =
    mensagem.texto || mensagem.body || mensagem.mensagem || mensagem.message;

  if (!texto) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.16 }}
      className="flex w-full justify-center"
    >
      <div className="max-w-[70%] rounded-full border border-slate-200 bg-white px-3 py-1.5 text-center text-[11px] font-semibold text-slate-500 shadow-sm">
        {texto}
      </div>
    </motion.div>
  );
}

function MensagemBloqueada({ empresaId, mensagemEmpresaId }) {
  return (
    <div className="flex w-full justify-center">
      <div className="inline-flex items-center gap-2 rounded-2xl border border-red-100 bg-red-50 px-3 py-2 text-xs font-medium text-red-600 shadow-sm">
        <FaLock size={12} />
        <span>Mensagem bloqueada por divergência de empresa.</span>

        <span className="hidden">
          empresa ativa: {empresaId} | empresa mensagem: {mensagemEmpresaId}
        </span>
      </div>
    </div>
  );
}

function CanalIcon({ canal, size = 14 }) {
  const normalized = String(canal || "").toLowerCase();

  if (normalized.includes("whatsapp")) {
    return <FaWhatsapp className="text-[#25D366]" size={size} />;
  }

  if (normalized.includes("instagram")) {
    return <FaInstagram className="text-[#C13584]" size={size} />;
  }

  if (normalized.includes("facebook") || normalized.includes("messenger")) {
    return <FaFacebookMessenger className="text-[#1877F2]" size={size} />;
  }

  if (normalized.includes("telegram")) {
    return <FaTelegramPlane className="text-[#229ED9]" size={size} />;
  }

  return <FaWhatsapp className="text-[#25D366]" size={size} />;
}

function getCanalStyle(canal) {
  const normalized = String(canal || "").toLowerCase();

  if (normalized.includes("whatsapp")) {
    return {
      clientAccent: "bg-[#25D366]",
      avatarBg: "bg-emerald-50 text-[#128C7E] ring-emerald-200",
      avatarRing: "ring-emerald-200",
      iconBg: "bg-emerald-50",
      clientAttachment:
        "bg-emerald-50 text-[#128C7E] ring-emerald-200 hover:bg-emerald-100",
    };
  }

  if (normalized.includes("instagram")) {
    return {
      clientAccent:
        "bg-gradient-to-b from-[#833AB4] via-[#E1306C] to-[#FCAF45]",
      avatarBg:
        "bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50 text-[#C13584] ring-pink-200",
      avatarRing: "ring-pink-200",
      iconBg: "bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50",
      clientAttachment:
        "bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50 text-[#C13584] ring-pink-200 hover:brightness-[0.98]",
    };
  }

  if (normalized.includes("facebook") || normalized.includes("messenger")) {
    return {
      clientAccent: "bg-[#1877F2]",
      avatarBg: "bg-blue-50 text-[#1877F2] ring-blue-200",
      avatarRing: "ring-blue-200",
      iconBg: "bg-blue-50",
      clientAttachment:
        "bg-blue-50 text-[#1877F2] ring-blue-200 hover:bg-blue-100",
    };
  }

  if (normalized.includes("telegram")) {
    return {
      clientAccent: "bg-[#229ED9]",
      avatarBg: "bg-sky-50 text-[#229ED9] ring-sky-200",
      avatarRing: "ring-sky-200",
      iconBg: "bg-sky-50",
      clientAttachment:
        "bg-sky-50 text-[#229ED9] ring-sky-200 hover:bg-sky-100",
    };
  }

  return {
    clientAccent: "bg-blue-600",
    avatarBg: "bg-blue-50 text-blue-700 ring-blue-200",
    avatarRing: "ring-blue-200",
    iconBg: "bg-blue-50",
    clientAttachment:
      "bg-blue-50 text-blue-700 ring-blue-200 hover:bg-blue-100",
  };
}

async function requestJsonMessage(path, options = {}) {
  const url = path.startsWith("http")
    ? path
    : `${API_PUBLIC_BASE || getApiPublicBase()}${path}`;

  const response = await fetch(url, {
    method: "GET",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...getAuthHeadersMessage(),
      ...(options.headers || {}),
    },
  });

  const text = await response.text();
  const data = text ? safeJsonMessage(text) : null;

  if (!response.ok) {
    const message =
      data?.erro ||
      data?.error ||
      data?.message ||
      text ||
      `HTTP ${response.status}`;

    throw new Error(message);
  }

  return data;
}

function getAuthHeadersMessage() {
  if (typeof window === "undefined") return {};

  const raw =
    window.localStorage?.getItem("token") ||
    window.localStorage?.getItem("authToken") ||
    window.localStorage?.getItem("jwt") ||
    "";

  const token = raw ? (raw.startsWith("Bearer ") ? raw : `Bearer ${raw}`) : "";

  return token ? { Authorization: token } : {};
}

function safeJsonMessage(text) {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

function isMensagemApagada(mensagem = {}) {
  const status = String(mensagem.status || "").toLowerCase();

  return (
    status === "apagada" ||
    status === "deleted" ||
    mensagem.apagada === true ||
    mensagem.deleted === true
  );
}

function isMensagemEditada(mensagem = {}) {
  const status = String(mensagem.status || "").toLowerCase();

  return (
    status === "editada" ||
    status === "edited" ||
    mensagem.editada === true ||
    mensagem.edited === true ||
    Boolean(mensagem.editada_em || mensagem.edited_at)
  );
}

function getApiPublicBase() {
  const fromEnv =
    typeof import.meta !== "undefined"
      ? import.meta.env?.VITE_API_PUBLIC_URL ||
        import.meta.env?.VITE_API_URL ||
        import.meta.env?.VITE_API_BASE_URL ||
        ""
      : "";

  const base = String(fromEnv || "").replace(/\/+$/, "");

  if (base) return base;

  if (typeof window !== "undefined") {
    const { protocol, hostname } = window.location;

    if (hostname.includes("captacrm.com.br")) {
      return `${protocol}//api.captacrm.com.br`;
    }

    return `${protocol}//${hostname}:3001`;
  }

  return "";
}

const API_PUBLIC_BASE = getApiPublicBase();

function getImagemUrl(value) {
  const url = String(value || "").trim();

  if (!url) return "";

  if (
    url.startsWith("http://") ||
    url.startsWith("https://") ||
    url.startsWith("data:image/") ||
    url.startsWith("blob:")
  ) {
    return url;
  }

  if (url.startsWith("//")) {
    return `https:${url}`;
  }

  if (url.startsWith("/storage/") || url.startsWith("/uploads/")) {
    return `${API_PUBLIC_BASE}${url}`;
  }

  if (url.startsWith("storage/") || url.startsWith("uploads/")) {
    return `${API_PUBLIC_BASE}/${url}`;
  }

  if (url.startsWith("/")) {
    return url;
  }

  return `${API_PUBLIC_BASE}/${url}`;
}

function getUsuarioLocalStorage() {
  if (typeof window === "undefined") return {};

  const keys = ["usuario", "user", "auth", "capta_usuario", "capta_user"];

  for (const key of keys) {
    try {
      const raw = window.localStorage.getItem(key);
      if (!raw) continue;

      const parsed = JSON.parse(raw);

      if (parsed?.usuario) return parsed.usuario;
      if (parsed?.user) return parsed.user;
      if (parsed?.data?.usuario) return parsed.data.usuario;
      if (parsed?.data?.user) return parsed.data.user;

      return parsed;
    } catch {
      // ignora localStorage inválido
    }
  }

  return {};
}

function isImageUrl(url) {
  const cleanUrl = String(url || "").split("?")[0];

  return /\.(png|jpe?g|gif|webp|bmp|svg|heic)$/i.test(cleanUrl);
}

function isVideoUrl(url) {
  const cleanUrl = String(url || "").split("?")[0];

  return /\.(mp4|mov|webm|mkv|avi)$/i.test(cleanUrl);
}

function isAudioUrl(url) {
  const cleanUrl = String(url || "").split("?")[0];

  return /\.(mp3|ogg|wav|m4a|webm|aac)$/i.test(cleanUrl);
}

function formatHora(value) {
  if (!value) return "";

  if (typeof value === "string" && value.length <= 8) {
    return value;
  }

  const data = new Date(value);

  if (Number.isNaN(data.getTime())) {
    return String(value);
  }

  return data.toLocaleTimeString("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
  });
}
